import {
  DEFAULT_SETTING_PC,
  DEFAULT_SETTING_REFLECTION,
  DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  MIN_PROGRESS_STARTED,
  SETTING_PC,
  SETTING_REFLECTION,
  SETTING_WORKFLOW_STOP_AFTER,
  SettingsResponsePC,
  SettingsResponseReflection,
  SettingsResponseWorkflowStopAfter,
} from "@pages/data-management/data-management-types";
import { getScanByFilename } from "@api/stagingarea-api/stagingarea-api";
import { GUID } from "@faro-lotv/ielement-types";
import {
  BackgroundTaskInfoType,
  BackgroundTaskState,
  CaptureTreePointCloudType,
  CaptureTreeEntity,
  CaptureTreeEntityType,
  RevisionStatus,
} from "@faro-lotv/service-wires";
import { CaptureTreeEntityWithRevisionStatus } from "@custom-types/capture-tree/capture-tree-types";
import { isScanFileUploadTaskContext, isPhotogrammetryUploadTaskContext } from "@custom-types/file-upload-type-guards";
import { FileUploadTask } from "@custom-types/file-upload-types";
import { AnyFilesInfo, TableItem, TableItemState, WorkflowState } from "@pages/data-management/data-management-types";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import { getScanUuid } from "@pages/data-management/import-data/import-data-utils";
import { RegisteredDataBase } from "@pages/data-management/registered-data/registered-data-types";
import { DeviceType, UploadedData } from "@pages/data-management/uploaded-data/uploaded-data-types";
import { runtimeConfig } from "@src/runtime-config";
import { APITypes, SphereDashboardAPITypes } from "@stellar/api-logic";
import { isScanEntity, isScanEntityType } from "@utils/capture-tree/capture-tree-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { ErrorHandlingFunction } from "@context-providers/error-boundary/error-handling-context";;
import { UploadElementType } from "@custom-types/file-upload-types";
import { SettingsApiClient, SettingsApiTypes } from "@stellar/settings-api-client";
import { ProjectId } from "@custom-types/base-types";

/** Specify base number to parse integers from */
const RADIX = 10;

/**
 * Constructs a URL for publishing a registration revision for a specified project.
 *
 * @param {ProjectId} projectId - The ID of the project for which the registration is being published.
 * @param {string} revisionId - The ID of the registration revision to be published.
 * @returns {string} A URL object pointing to the endpoint for publishing the specified registration revision.
 */
export function getInspectAndPublishToolUrl(
  projectId: ProjectId,
  revisionId: string,
  additionalParams?: URLSearchParams
): string {
  const url = new URL(
    `/data-preparation/project/${projectId}`,
    runtimeConfig.urls.sphereViewerUrl
  );
  url.searchParams.append("revisionId", revisionId);

  if (additionalParams) {
    for (const [key, value] of additionalParams) {
      url.searchParams.append(key, value);
    }
  }

  return url.href;
}

/**
 * @returns The UUID of a raw ELS, FLS or Orbis scan, as defined by the scanner. Undefined otherwise.
 *          For older ELS scans uploaded by Staging Area, the externalId may be a random GUID instead.
 */
export function getExternalScanId(scan: CaptureTreeEntity): string | undefined {
  if (isScanEntity(scan) && scan.pointClouds) {
    const { elsRaw, flsRaw, orbisRaw } = CaptureTreePointCloudType;
    const scansWithExternalId = [elsRaw, flsRaw, orbisRaw];
    for (const pointCloud of scan.pointClouds) {
      if (scansWithExternalId.includes(pointCloud.type) && pointCloud.externalId) {
        return pointCloud.externalId;
      }
    }
  }
  return undefined;
}

/**
 * Returns a map of the externalIds of the uploaded ELS scans.
 * @param uploadedEntities Array of uploaded entities returned by uploadedDataSelector.
 */
export function getUploadedIdsMap(uploadedEntities: UploadedData[]): { [key: string]: boolean } {
  const idMap: { [key: string]: boolean } = {};
  for (const uploadedEntity of uploadedEntities) {
    const externalId = getExternalScanId(uploadedEntity);
    if (externalId) {
      idMap[externalId] = true;
    }
  }
  return idMap;
}

/**
 * Returns the upload tasks for photogrammetry scans to remove if a corresponding processing background task exists,
 * and those to keep otherwise.
 * @param uploadTasks Array of upload tasks for the current project.
 * @param uploadedIdsMap Result of the getUploadedIdsMap function.
 * @param backgroundTasks Array of background tasks for the current project.
 */
export function getUploadTasksToKeepOrRemove(
  uploadTasks: FileUploadTask[],
  uploadedIdsMap: { [key: APITypes.UUID]: boolean },
  backgroundTasks: SdbBackgroundTask[]
): {
  uploadTasksToKeep: FileUploadTask[];
  uploadTasksToRemove: FileUploadTask[];
} {
  const { uploadTasksToKeep, uploadTasksToRemove } =
    getUploadScanTasksToKeepOrRemove(uploadTasks, uploadedIdsMap);
  const { uploadedPhotogrammetryTasksToKeep, uploadedPhotogrammetryTasksToRemove } =
    getUploadedPhotogrammetryTasksToKeepOrRemove(uploadTasks, backgroundTasks);

  return {
    uploadTasksToKeep: [
      ...uploadTasksToKeep,
      ...uploadedPhotogrammetryTasksToKeep,
    ],
    uploadTasksToRemove: [
      ...uploadTasksToRemove,
      ...uploadedPhotogrammetryTasksToRemove,
    ],
  };
}

/**
 * Returns the upload tasks for scans to remove since associated entities now exist, and those to keep.
 * @param uploadTasks Array of upload tasks for the current project.
 * @param uploadedIdsMap Result of the getUploadedIdsMap function.
 */
function getUploadScanTasksToKeepOrRemove(
  uploadTasks: FileUploadTask[],
  uploadedIdsMap: { [key: APITypes.UUID]: boolean }
): {
  uploadTasksToKeep: FileUploadTask[];
  uploadTasksToRemove: FileUploadTask[];
} {
  const uploadTasksToKeep: FileUploadTask[] = [];
  const uploadTasksToRemove: FileUploadTask[] = [];

  for (const task of uploadTasks) {
    if (isScanFileUploadTaskContext(task.context)) {
      const id = getScanUuid(task.fileName, task.context.lsDataV2);
    if (id && uploadedIdsMap[id]) {
      uploadTasksToRemove.push(task);
    } else {
      uploadTasksToKeep.push(task);
    }
  }
  }

  return { uploadTasksToKeep, uploadTasksToRemove };
}

/**
 * Returns the upload tasks for photogrammetry scans to remove since associated entities now exist, and those to keep.
 * @param uploadTasks Array of upload tasks for the current project.
 * @param backgroundTasks Array of background tasks for the current project.
 */
function getUploadedPhotogrammetryTasksToKeepOrRemove(
  uploadTasks: FileUploadTask[],
  backgroundTasks: SdbBackgroundTask[]
): {
  uploadedPhotogrammetryTasksToKeep: FileUploadTask[];
  uploadedPhotogrammetryTasksToRemove: FileUploadTask[];
} {
  const uploadedPhotogrammetryTasksToKeep: FileUploadTask[] = [];
  const uploadedPhotogrammetryTasksToRemove: FileUploadTask[] = [];
  const validBackgroundTasks = backgroundTasks.filter(
    (task) => task.state && task.taskType === "Photogrammetry"
  );
  for (const task of uploadTasks) {
    if (isPhotogrammetryUploadTaskContext(task.context)) {
      const scanId = task.context.scanId;
      if (
        !validBackgroundTasks.find(
          (backgroundTask) => backgroundTask.context?.elementId === scanId
        )
      ) {
        uploadedPhotogrammetryTasksToKeep.push(task);
      } else {
        uploadedPhotogrammetryTasksToRemove.push(task);
      }
    }
  }

  return {
    uploadedPhotogrammetryTasksToKeep,
    uploadedPhotogrammetryTasksToRemove,
  };
}

/**
 * Creates a table item for a Photogrammetry scan upload task.
 * @param task Upload task for a Photogrammetry scan
 * @param itemState The state of the table item
 * @param progress The upload progress
 * @param currentUser Current user returned by currentUserSelector
 * @returns TableItem for a Photogrammetry scan
 */
function createTableItemFromPhotogrammetryUpload(
  task: FileUploadTask,
  itemState: TableItemState,
  progress: number,
  currentUser: SphereDashboardAPITypes.ICompanyMemberBase | null
): TableItem {
  return {
    id: task.id,
    type: "Upload",
    name: task.fileName,
    createdAt: task.createdAt ?? "1970-01-01T00:00:00.000Z",
    createdBy: currentUser?.id ?? "",
    deviceType: CaptureTreeEntityType.photogrammetryScan,
    state: itemState,
    progress,
  };
}

/**
 * Creates a table item for a scan upload task.
 * - ElsScans (Blink)
 * - FocusScans
 * - OrbisScans
 *
 * @param task Upload task for a scan
 * @param itemState The state of the table item
 * @param progress The upload progress
 * @param currentUser Current user returned by currentUserSelector
 * @returns TableItem for a scan
 */
function createTableItemFromScanUpload(
  task: FileUploadTask,
  itemState: TableItemState,
  progress: number,
  currentUser: SphereDashboardAPITypes.ICompanyMemberBase | null
): TableItem {

  const lsDataV2 = isScanFileUploadTaskContext(task.context) ? task.context.lsDataV2 : null;
  const scan = getScanByFilename(task.fileName, lsDataV2);

  const captureTreeEntityType = getCaptureTreeEntityTypeFromUploadElementType(task.context.uploadElementType);

  const item: TableItem = {
    id: task.id,
    type: "Upload",
    name: scan?.name || task.fileName,
    createdAt: scan?.recordingTime ?? "1970-01-01T00:00:00.000Z",
    // Since it's only possible to see the uploads from the current user then one can correctly assume that all
    // local uploads are from the current user.
    createdBy: currentUser?.id ?? "",
    deviceType: captureTreeEntityType,
    state: itemState,
    progress,
  };

  return item;
}

/**
 * Creates table entries for the scan table shown in the Staging Area based on the provided upload tasks.
 * @param uploadTasks Array of upload tasks for the current project.
 * @param currentUser Current user returned by currentUserSelector.
 * @returns Object that also contains if at least one upload task is currently active and if there is at least one
 *          upload error.
 */
export function getTableItemsFromTasks(
  uploadTasks: FileUploadTask[],
  currentUser: SphereDashboardAPITypes.ICompanyMemberBase | null
) : {
  tableItemsFromTasks: TableItem[],
  hasUploadTaskInProgress: boolean,
  hasUploadTaskSucceeded: boolean,
  hasUploadTaskWithError: boolean,
} {
  const tableItemsFromTasks: TableItem[] = [];
  let hasUploadTaskInProgress = false;
  let hasUploadTaskSucceeded = false;
  let hasUploadTaskWithError = false;

  for (const task of uploadTasks) {
    const progress = getUploadProgressFromTask(task);

    let itemState: TableItemState;
    switch (task.status) {
      case BackgroundTaskState.created:
      case BackgroundTaskState.started:
        if (0 < progress) {
          hasUploadTaskInProgress = true;
          itemState = "uploading";
        } else {
          itemState = "start";
        }
        break;
      case BackgroundTaskState.succeeded:
        // It takes a while until a finished upload task results in an added entity to uploadedEntities.
        // We have to handle this by marking the upload as still in progress for the overall state.
        hasUploadTaskSucceeded = true;
        itemState = "uploaded";
        break;
      case BackgroundTaskState.failed:
        itemState = "error";
        hasUploadTaskWithError = true;
        break;
      case BackgroundTaskState.aborted:
        // User-aborted tasks are not considered as upload errors so we don't set hasUploadErrorFromTasks to true here.
        // It doesn't seem necessary to show them in the table, since the user aborted them himself in the current tab.
        continue;
      case BackgroundTaskState.scheduled:
      default:
        itemState = "start";
        break;
    }

    if (isScanFileUploadTaskContext(task.context)) {
      const item = createTableItemFromScanUpload(task, itemState, progress, currentUser);
      tableItemsFromTasks.push(item);
    } else if (isPhotogrammetryUploadTaskContext(task.context)) {
      const item = createTableItemFromPhotogrammetryUpload(task, itemState, progress, currentUser);
      tableItemsFromTasks.push(item);
  }
  }

  return {
    tableItemsFromTasks,
    hasUploadTaskInProgress,
    hasUploadTaskSucceeded,
    hasUploadTaskWithError,
  };
}

/**
 * Creates a table item for a scan entity.
 * @param upload The uploaded scan entity
 * @param itemState The state of the table item
 * @returns TableItem for a scan entity
 */
function createTableItemFromScanEntity(
  upload: UploadedData,
  itemState: TableItemState
): TableItem {
  return {
    id: upload.id,
    type: "Scan",
    name: upload.name,
    createdAt: upload.createdAt,
    createdBy: upload.createdBy,
    deviceType: upload.type,
    state: itemState,
  };
}

/**
 * Creates table entries for the scan table shown in the Staging Area based on the provided uploaded entities.
 * @param uploadedEntities Array of uploaded entities returned by uploadedDataSelector.
 * @param state State of the Staging Area workflow.
 */
export function getTableItemsFromEntities(
  state: WorkflowState,
  uploadedEntities: UploadedData[]
): TableItem[] {
  const tableItemsFromUploads: TableItem[] = [];

  for (const upload of uploadedEntities) {
    let itemState: TableItemState;
    if (upload.isProcessing) {
      itemState = "processing";
    } else if (upload.hasTaskErrors) {
      itemState = "error";
    } else if (state === "published") {
      itemState = "published";
    } else {
      itemState = "unpublished";
    }
    const tableItem = createTableItemFromScanEntity(upload, itemState);
    tableItemsFromUploads.push(tableItem);
  }

  return tableItemsFromUploads;
}

/**
 * Returns the progress of the upload task.
 * @param uploadTask Upload task for the current project.
 */
function getUploadProgressFromTask(uploadTask: FileUploadTask): number {
  switch (uploadTask.status) {
    case BackgroundTaskState.succeeded:
    case BackgroundTaskState.aborted:
    case BackgroundTaskState.failed:
      // We treat errors as the task having finished for the sake of progress reporting.
      return 100;
    case BackgroundTaskState.created:
    case BackgroundTaskState.scheduled:
    case BackgroundTaskState.started:
    default:
      return uploadTask.progress ?? 0;
  }
}

/**
 * Returns the progress of the upload tasks by calculating the average progress value.
 * This wouldn't work well if the uploaded files had very different file sizes. But this shouldn't be much of a problem
 * for our use case in the Staging Area, so it should be a reasonably good heuristic.
 * @param uploadTasks Array of upload tasks for the current project.
 */
function getUploadProgressFromTasks(uploadTasks: FileUploadTask[]): number {
  if (!uploadTasks.length) {
    return 0;
  }

  let sumProgress = 0;
  for (const uploadTask of uploadTasks) {
    sumProgress += getUploadProgressFromTask(uploadTask);
  }
  return sumProgress / uploadTasks.length;
}

/**
 * Sums up the upload speeds of all started tasks of the given upload tasks.
 * @param uploadTasks Array of upload tasks for the current project.
 * @returns Upload speed in MB/s.
 */
export function getUploadSpeed(uploadTasks: FileUploadTask[]): number {
  let sumSpeed = 0;
  for (const uploadTask of uploadTasks) {
    if (uploadTask.status === BackgroundTaskState.started) {
      sumSpeed += (uploadTask.speedMBps ?? 0);
    }
  }
  return sumSpeed;
}

/**
 * Returns the upload progress.
 * @param state State of the Staging Area workflow.
 * @param uploadTasks Array of upload tasks for the current project.
 */
export function getUploadProgress(state: WorkflowState, uploadTasks: FileUploadTask[]): number {
  switch (state) {
    case "start":
    case "startError":
      return 0;
    case "uploading":
    case "uploadError": {
      const progress = getUploadProgressFromTasks(uploadTasks);
      // Previously, we showed 99% progress because there can be a variable delay between all upload tasks reaching
      // 100% and the processing step actually starting. However, this was confusing since it seemed that not all
      // bytes were uploaded yet (the uploaded bytes are calculated from the upload percentage). It's clearer to show
      // 100% and inform the user that the upload process is in a "Finishing" step.
      return progress;
    }
    case "uploaded":
    case "processing":
    case "processError":
    case "processed":
    case "registering":
    case "registerError":
    case "registerBadResult":
    case "registered":
    case "publishing":
    case "publishError":
    case "published":
      return 100;
    default:
      return 0;
  }
}

/**
 * Returns the processing progress.
 * @param state State of the Staging Area workflow.
 * @param captureTreeOpenDraft Capture tree entities for the current draft revision of the selected project.
 * @param tasks Array of background tasks for the current project.
 * @returns Progress [percent].
 */
export function getProcessProgress(
  state: WorkflowState,
  captureTreeOpenDraft: CaptureTreeEntityWithRevisionStatus[],
  tasks: SdbBackgroundTask[]
): number {
  switch (state) {
    case "start":
    case "startError":
    case "uploading":
    case "uploadError":
    case "uploaded":
    case "processError":
      return 0;
    case "processing":
      return getProcessProgressFromTasks(captureTreeOpenDraft, tasks);
    case "processed":
    case "registering":
    case "registerError":
    case "registerBadResult":
    case "registered":
    case "publishing":
    case "publishError":
    case "published":
      return 100;
    default:
      return 0;
  }
}

/**
 * Returns the progress of the provided registration task.
 * @param registrationRevision Newest registration backend revision = registeredDataSelector()[0].
 * @returns Progress [percent].
 */
function getRegisterProgressFromTask(registrationRevision?: RegisteredDataBase): number {
  const task = registrationRevision?.task;
  if (!task) {
    return 0;
  }

  switch (task.state) {
    case "Succeeded":
    case "Aborted":
    case "Failed":
      // We treat errors as the task having finished for the sake of the progress bar.
      return 100;
    case "Pending":
    case "Created":
    case "Scheduled":
    case "Started":
    default: {
      const minProgress = task.state === BackgroundTaskState.started ? MIN_PROGRESS_STARTED : 0;
      // Cut off the % suffix added by generateSdbBackgroundTaskProgress.
      if (task.progress?.endsWith("%")) {
        const progressStr = task.progress.substring(0, task.progress.length - 1);
        return Math.max(minProgress, parseInt(progressStr, RADIX));
      } else {
        return minProgress;
      }
    }
  }
}

/**
 * Returns the registration progress.
 * @param state State of the Staging Area workflow.
 * @param registrationRevision Newest registration backend revision = registeredDataSelector()[0].
 */
export function getRegisterProgress(state: WorkflowState, registrationRevision?: RegisteredDataBase): number {
  switch (state) {
    case "start":
    case "startError":
    case "uploading":
    case "uploadError":
    case "uploaded":
    case "processing":
    case "processError":
    case "registerError":
    case "registerBadResult":
    case "processed":
      return 0;
    case "registering":
      return getRegisterProgressFromTask(registrationRevision);
    case "registered":
    case "publishing":
    case "publishError":
    case "published":
      return 100;
    default:
      return 0;
  }
}

/**
 * @returns The device type for a capture tree entity type
 *
 * @param captureTreeEntityType Capture tree entity type
 */
export function getDeviceType(
  captureTreeEntityType: CaptureTreeEntityType
): DeviceType {
  switch (captureTreeEntityType) {
    case CaptureTreeEntityType.elsScan:
      return DeviceType.blink;
    case CaptureTreeEntityType.focusScan:
      return DeviceType.focus;
    case CaptureTreeEntityType.orbisScan:
    case CaptureTreeEntityType.orbisFlashScan:
      return DeviceType.orbis;
    case CaptureTreeEntityType.siteScapeScan:
      return DeviceType.sitescape;
    case CaptureTreeEntityType.photogrammetryScan:
      return DeviceType.photogrammetry;
    case CaptureTreeEntityType.root:
    case CaptureTreeEntityType.cluster:
    case CaptureTreeEntityType.pCloudUploadScan:
    case CaptureTreeEntityType.structuredScan:
    case CaptureTreeEntityType.unstructuredScan:
    case undefined:
      return DeviceType.unknown;
    default:
      captureTreeEntityType satisfies never;
      return DeviceType.unknown;
  }
}

/**
 * Returns the capture tree entity type for the given upload element type.
 * @param uploadElementType
 * @returns
 */
function getCaptureTreeEntityTypeFromUploadElementType(
  uploadElementType: UploadElementType
): CaptureTreeEntityType {
  switch (uploadElementType) {
    case UploadElementType.elsScan:
      return CaptureTreeEntityType.elsScan;
    case UploadElementType.focusScan:
      return CaptureTreeEntityType.focusScan;
    case UploadElementType.photogrammetry:
      return CaptureTreeEntityType.photogrammetryScan;
    case UploadElementType.orbisScan:
      return CaptureTreeEntityType.orbisScan;
    case UploadElementType.annotation:
    case UploadElementType.default:
    case UploadElementType.group:
    case UploadElementType.project:
      return CaptureTreeEntityType.elsScan;
    default:
      uploadElementType satisfies never;
      return CaptureTreeEntityType.elsScan;
  }
}

/**
 * Map background tasks by element ID.
 * @param tasks Array of background tasks for the current project.
 */
function getTasksByElementId(tasks: SdbBackgroundTask[]): Record<string, SdbBackgroundTask> {
  const tasksByElementId: Record<string, SdbBackgroundTask> = {};
  for (const task of tasks) {
    if (task.context?.elementId) {
      tasksByElementId[task.context.elementId] = task;
    }
  }
  return tasksByElementId;
}

/**
 * Get all pointcloud IDs for the given pointcloud type from a capture tree.
 * @param captureTree Capture tree entities for the selected project.
 * @param pcType Pointcloud type to filter.
 */
function getPointCloudsByType(captureTree: CaptureTreeEntity[], pcType: CaptureTreePointCloudType): string[] {
  const pointClouds: string[] = [];
  for (const node of captureTree) {
    if (node.pointClouds) {
      for (const pointCloud of node.pointClouds) {
        if (pointCloud.type === pcType) {
          pointClouds.push(pointCloud.id);
        }
      }
    }
  }
  return pointClouds;
}

/**
 * Get the progress of all tasks that are running for the given pointcloud IDs.
 * @param tasksByElementId Background tasks for the current project.
 * @param pointCloudIds The IDs of the pointclouds belonging to a scans of the capture tree.
 * @returns The progress and total number of tasks.
 */
function getBackgroundTaskProgress(
  tasksByElementId: { [id: GUID]: SdbBackgroundTask },
  pointCloudIds: string[]
): { progress: number, total: number, hasStarted: boolean } {
  // Default value is correct for ProcessElsRawScan. It is used when a task is missing to have the correct total.
  let defaultTotal = 10;
  let progress = 0;
  let total = 0;
  let hasStarted = false;

  // 1st pass: Find the default total value.
  for (const pointCloudId of pointCloudIds) {
    const task = tasksByElementId[pointCloudId];
    // Update the default, in case it is different. "> 0" should be always true, but who knows.
    if (task?.status?.type === BackgroundTaskInfoType.progress &&
      task?.status?.progress?.total &&
      task?.status?.progress?.total > 0
    ) {
      defaultTotal = task.status.progress.total;
      break;
    }
  }

  // 2nd pass: Calculate the progress and total values.
  for (const pointCloudId of pointCloudIds) {
    const task = tasksByElementId[pointCloudId];
    if (task) {
      if (task.status?.type === BackgroundTaskInfoType.progress && task.status.progress) {
        progress += task.status.progress.current;
        total += task.status.progress.total;
        hasStarted = hasStarted || task.state === BackgroundTaskState.started;
      } else if (task.status?.type === BackgroundTaskInfoType.state) {
        switch (task.status.state) {
          // Consistent with the other progress calculations, count failed tasks as finished.
          // That prevents the progress value from going backwards.
          case "Succeeded":
          case "Aborted":
          case "Failed":
            progress += defaultTotal;
            total += defaultTotal;
            break;
          case "Created":
          case "Scheduled":
          case "Started":
            total += defaultTotal;
            break;
          default:
            task.status.state satisfies never;
            total += defaultTotal;
            break;
        }
        hasStarted = hasStarted || task.status.state === BackgroundTaskState.started;
      }
    } else {
      total += defaultTotal;
    }
  }

  // Handle Photogrammetry tasks
  for (const task of Object.values(tasksByElementId)) {
    if (task.taskType !== "Photogrammetry") {
      continue;
    }

    hasStarted = hasStarted || task.state === BackgroundTaskState.started;
    if (task.status?.type === BackgroundTaskInfoType.progress && task.status.progress) {
      progress += task.status.progress.current;
      total += task.status.progress.total;
    }
  }

  return {
    // Make sure to never report more than 100% progress.
    progress: Math.min(progress, total),
    total,
    hasStarted,
  };
}

/**
 * Returns the progress percentage of processing tasks for the given scans in the capture tree.
 * @param captureTreeOpenDraft Capture tree entities for the current draft revision of the selected project.
 * @param tasks Array of background tasks for the current project.
 */
function getProcessProgressFromTasks(
  captureTreeOpenDraft: CaptureTreeEntityWithRevisionStatus[],
  tasks: SdbBackgroundTask[]
): number {
  const tasksByElementId = getTasksByElementId(tasks);
  // Filter out unchanged entities.
  const changedCaptureTreeEntities = captureTreeOpenDraft.filter((entity) => entity.status !== RevisionStatus.initialized);
  const elsPointClouds = getPointCloudsByType(changedCaptureTreeEntities, CaptureTreePointCloudType.elsRaw);
  const progressResult = getBackgroundTaskProgress(tasksByElementId, elsPointClouds);

  const minProgress = progressResult.hasStarted ? MIN_PROGRESS_STARTED : 0;
  // Return the progress percentage, as a number between 0 and 100.
  if (progressResult.total === 0) {
    return minProgress;
  }
  return Math.max(minProgress, Math.floor(progressResult.progress / progressResult.total * 100));
}

/** Returns whether the provided table item can be removed. */
export function canTableItemBeRemoved(item: TableItem): boolean {
  return item.type === "Scan" && item.state !== "uploading" && item.state !== "processing";
}

/** Returns the capture tree entities of scans to be removed. */
export function getScanEntitiesToRemove(
  scansToRemove: TableItem[],
  treeElements: CaptureTreeEntity[]
): CaptureTreeEntity[] {
  // Find the corresponding capture tree entities for the selected table items.
  const treeMap = new Map<string, CaptureTreeEntity>();
  for (const treeElement of treeElements) {
    if (isScanEntityType(treeElement.type)) {
      treeMap.set(treeElement.id, treeElement);
    }
  }

  const treeEntitiesToRemove: CaptureTreeEntity[] = [];
  for (const scan of scansToRemove) {
    const treeElement = treeMap.get(scan.id);
    if (treeElement) {
      treeEntitiesToRemove.push(treeElement);
    }
  }
  return treeEntitiesToRemove;
}


/** Returns the settings for the checkboxes in the publish options, with fallback settings in error case. */
export async function getPublishSettings(
  projectId: ProjectId,
  settingsApiClient: SettingsApiClient
): Promise<{
  settingPC: SettingsResponsePC,
  settingReflection: SettingsResponseReflection,
  settingWorkflowStopAfter: SettingsResponseWorkflowStopAfter,
}> {
  // For errors, we return the assumed default value. If the setting was never written, the modifiedAt date
  // returned by CoreAPI would be the Unix epoch.
  const defaultModifiedAt = "1970-01-01T00:00:00Z";

  const promisePC = settingsApiClient.project.fetchProjectSettings(projectId.toString(), SETTING_PC).catch((error) => {
    sentryCaptureError({ error });
    return ({
      // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
      value: DEFAULT_SETTING_PC,
      modifiedAt: defaultModifiedAt,
    }) as SettingsResponsePC;
  });

  const promiseReflection = settingsApiClient.project.fetchProjectSettings(
    projectId.toString(),
    SETTING_REFLECTION
  ).catch((error) => {
    sentryCaptureError({ error });
    return ({
      // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
      value: DEFAULT_SETTING_REFLECTION,
      modifiedAt: defaultModifiedAt,
    }) as SettingsResponseReflection;
  });

  const promiseWorkflowStopAfter = settingsApiClient.project.fetchProjectSettings(
    projectId.toString(),
    SETTING_WORKFLOW_STOP_AFTER
  ).catch((error) => {
    sentryCaptureError({ error });
    return ({
      value: DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
      modifiedAt: defaultModifiedAt,
    }) as SettingsResponseWorkflowStopAfter;
  });

  const [settingPC, settingReflection, settingWorkflowStopAfter] = await Promise.all([
    promisePC, promiseReflection, promiseWorkflowStopAfter,
  ]);
  return {
    settingPC,
    settingReflection,
    settingWorkflowStopAfter,
  };
}

/**
 * Forcefully sets a project setting, even if there were concurrent writes by other clients that we haven't seen yet.
 * E.g. for a boolean/checkbox setting, trying any conflict resolution seems overkill, therefore this simple approach.
 * @param settingsApiClient
 * @param projectId
 * @param identifier
 * @param body New `value` to set, and the same `modifiedAt` that you previously received from the `getProjectSetting` call
 *        for the same setting.
 * @returns Updated setting info.
 * @throws {CoreAPITypes.IResponseError} 404 if the project or setting does not exist, or 400, or 401.
 */
export async function forceSetProjectSetting<
  Identifier extends SettingsApiTypes.SettingsIdentifierByScope<"PROJECT">
>(
  settingsApiClient: SettingsApiClient,
  projectId: ProjectId,
  identifier: Identifier,
  body: SettingsApiTypes.UpdateSettingPayload<"PROJECT", Identifier>
): Promise<SettingsApiTypes.SettingsResponse<"PROJECT", Identifier>> {
  // Remove any extra properties from the body, since e.g. a SettingsResponse could have been used.
  body = { value: body.value, modifiedAt: body.modifiedAt };
  
  try {
    return await settingsApiClient.project.updateProjectSettings(
      projectId.toString(),
      identifier,
      body
    );
  } catch (error) {
    try {
      const currentSetting = await settingsApiClient.project.fetchProjectSettings(projectId.toString(), identifier);
      return await settingsApiClient.project.updateProjectSettings(
        projectId.toString(),
        identifier,
        { ...body, modifiedAt: currentSetting.modifiedAt }
      );
    } catch {
      throw error;
    }
  }
}

/** Saves the publish settings, shows a toast on error, or returns true on success. */
export async function setPublishSettings(
  projectId: ProjectId,
  settingsApiClient: SettingsApiClient,
  handleErrorWithToast: ErrorHandlingFunction,
  {
    settingPC,
    settingReflection,
    settingWorkflowStopAfter,
  }: {
    settingPC: SettingsApiTypes.UpdateSettingPayload<"PROJECT", typeof SETTING_PC>,
    settingReflection: SettingsApiTypes.UpdateSettingPayload<"PROJECT", typeof SETTING_REFLECTION>,
    settingWorkflowStopAfter: SettingsApiTypes.UpdateSettingPayload<"PROJECT", typeof SETTING_WORKFLOW_STOP_AFTER>,
  }
): Promise<boolean> {
  try {
    await Promise.all([
      forceSetProjectSetting(settingsApiClient, projectId, SETTING_PC, settingPC),
      forceSetProjectSetting(settingsApiClient, projectId, SETTING_REFLECTION, settingReflection),
      forceSetProjectSetting(settingsApiClient, projectId, SETTING_WORKFLOW_STOP_AFTER, settingWorkflowStopAfter),
    ]);
    return true;
  } catch (error) {
    handleErrorWithToast({
      id: `setPublishSettings-${Date.now().toString()}`,
      title: "Could not save settings",
      error,
    });
    return false;
  }
}

/** Returns a map with the the provided file info objects as keys and their file sizes in bytes as values. */
export function getFileSizeForFileInfos(fileInfos: AnyFilesInfo[]): Map<AnyFilesInfo, number> {
  const result = new Map<AnyFilesInfo, number>();
  for (const fileInfo of fileInfos) {
    let size: number = 0;
    for (const file of fileInfo.filesToUpload) {
      size += file.size;
    }
    result.set(fileInfo, size);
  }
  return result;
}

/** Returns the total file size in bytes of the files contained in the file info objects. */
export function getTotalFileSizeOfFileInfos(fileInfos: AnyFilesInfo[]): number {
  const sizes = getFileSizeForFileInfos(fileInfos);
  let total = 0;
  for (const size of sizes.values()) {
    total += size;
  }
  return total;
}
